/**
 * SMS Queue Modal
 * View and edit SMS queue items
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { Stack, Group, Button, Alert, Text, TextInput, Badge, Textarea, Modal, Paper } from '../../../../../components/ui';
import { StatusTracker } from '../../../../../components/advanced/workflow';
import type { SmsQueueModalProps } from '../types';
import { decodeSmsMessage, getCharacterCount } from '../types';
import { isValidPhone } from '../../_shared/phone';
import { getStatusColor, formatDateTime } from '../../types';

export function SmsQueueModal({
  opened,
  onClose,
  item,
  mode,
  onSave,
  loading,
}: SmsQueueModalProps) {
  const isEditMode = mode === 'edit';

  const form = useForm({
    initialValues: {
      cellNumber: '',
      message: '',
    },
    validate: {
      cellNumber: (value) => {
        if (!isValidPhone(value)) {
          return 'Enter a valid number, e.g. 0794559874 or +27794559874';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (item) {
      const decodedMessage = decodeSmsMessage(item.message);
      form.setValues({
        cellNumber: item.cellNumber || '',
        message: decodedMessage,
      });
    }
  }, [item]);

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave && item) {
      await onSave({
        ...item,
        ...values,
      } as any);
      onClose();
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!item) return null;

  const decodedMessage = decodeSmsMessage(item.message);
  const charInfo = getCharacterCount(decodedMessage);

  const statusSteps = [
    {
      key: 'queued',
      label: 'Queued',
      description: 'Added to queue',
      timestamp: item.createdDateTime ? new Date(item.createdDateTime) : undefined,
    },
    {
      key: 'sent',
      label: 'Sent',
      description: 'Sent to provider',
      timestamp: item.dateSent ? new Date(item.dateSent) : undefined,
    },
  ];

  const currentStep = item.status?.toLowerCase() || 'queued';
  const completedSteps = [];
  if (item.createdDateTime) completedSteps.push('queued');
  if (item.dateSent) completedSteps.push('sent');

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? 'Edit SMS' : 'SMS Details'}
      
      size="lg"
    >
      <Stack gap="md">
        <Group>
          <Badge
            
            gradientVariant={getStatusColor(item.status as any)}
            size="lg"
          >
            {item.status}
          </Badge>
          {item.retryCount > 0 && (
            <Badge variant="light" color="orange">
              Retry {item.retryCount}
            </Badge>
          )}
        </Group>

        <Paper p="md" withBorder>
          <StatusTracker
            title="Delivery Status"
            variant="timeline"
            steps={statusSteps}
            currentStatus={currentStep}
            completedSteps={completedSteps}
            failedStep={item.status === 'Failed' ? currentStep : undefined}
            showTimestamps
            orientation="vertical"
          />
        </Paper>

        {item.status === 'Failed' && item.errorMessage && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Error"
          >
            {item.errorMessage}
          </Alert>
        )}

        {isEditMode ? (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Phone Number"
                required
                {...form.getInputProps('cellNumber')}
              />

              <Textarea
                label="Message"
                required
                rows={6}
                {...form.getInputProps('message')}
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading} variant="light">
                  Save Changes
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="md">
            <div>
              <Text fw={500} size="sm" mb={4}>
                Phone Number
              </Text>
              <Text size="sm" ff="monospace">{item.cellNumber}</Text>
            </div>

            <div>
              <Text fw={500} size="sm" mb={4}>
                Message
              </Text>
              <Paper p="md" withBorder>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{decodedMessage}</Text>
              </Paper>
              <Group gap="xs" mt="xs">
                <Badge size="sm" variant="light">
                  {charInfo.count} characters
                </Badge>
                <Badge size="sm" variant="light" color={charInfo.segments > 1 ? 'orange' : 'green'}>
                  {charInfo.segments} segment{charInfo.segments > 1 ? 's' : ''}
                </Badge>
              </Group>
            </div>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={handleClose}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
